import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddScenarioContainerDialog from "../components/utilities/AddScenarioContainerDialog";
import { deleteContainer } from "../api/stationsApi";
import api from "../../../API/interceptors";
import { Footer } from "../../../Components/Footer";

import { useTheme } from "@mui/material/styles";

const asArray = (value) => (Array.isArray(value) ? value : []);
const formatDate = (value) => {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime())
    ? date.toLocaleString("pl-PL")
    : "Brak danych";
};
const scrollStyles = {
  overflowY: "auto",
  minHeight: 0,
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#94a3b8", borderRadius: 8 },
};

function Metadata({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {children ?? "Brak danych"}
      </Typography>
    </Box>
  );
}

export default function ScenarioList({ setSelectedScenario }) {
  const navigate = useNavigate();
  const { accessToken: token } = useSelector((state) => state.auth);
  const [myScenarios, setMyScenarios] = useState([]);
  const [allScenarios, setAllScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [activeTab, setActiveTab] = useState("my");

  const theme = useTheme();

  const fetchScenarios = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [myRes, allRes] = await Promise.all([
        api(
          `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/containers`,
        ),
        api(
          `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/all-containers`,
        ),
      ]);
      const myData = Array.isArray(myRes) ? myRes : myRes?.data;
      const allData = Array.isArray(allRes) ? allRes : allRes?.data;
      if (!Array.isArray(myData) || !Array.isArray(allData)) {
        throw new Error("API nie zwróciło tablic scenariuszy");
      }
      setMyScenarios(myData);
      setAllScenarios(allData);
    } catch (err) {
      console.error("Error fetching scenarios:", err);
      setError("Nie udało się pobrać scenariuszy. Spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const deleteScenario = async (id) => {
    setDeletingId(id);
    setError("");
    try {
      await deleteContainer(id);
      setMyScenarios((items) => items.filter((item) => item.id !== id));
      setAllScenarios((items) => items.filter((item) => item.id !== id));
    } catch (err) {
      console.error("Error deleting scenario:", err);
      setError("Nie udało się usunąć scenariusza. Spróbuj ponownie.");
    } finally {
      setDeletingId(null);
    }
  };

  const scenarios = activeTab === "my" ? myScenarios : allScenarios;

  return (
    <>
      <Box sx={{ maxWidth: 1100, mx: "auto", mb: 4, p: { xs: 1.5, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="overline"
              color="primary.main"
              sx={{ letterSpacing: 2 }}
            >
              VR EDITOR
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Scenariusze
            </Typography>
            <Typography color="text.secondary">
              Zarządzaj scenariuszami, stacjami i obiektami.
            </Typography>
          </Box>
          {activeTab === "my" && (
            <AddScenarioContainerDialog
              token={token}
              onSuccess={fetchScenarios}
            />
          )}
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab value="my" label={`Moje scenariusze (${myScenarios.length})`} />
          <Tab
            value="all"
            label={`Wszystkie scenariusze (${allScenarios.length})`}
          />
        </Tabs>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                disabled={loading}
                onClick={fetchScenarios}
              >
                Odśwież
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Box
          role="region"
          aria-label="Lista scenariuszy"
          tabIndex={0}
          aria-busy={loading}
          sx={{
            ...scrollStyles,
            maxHeight: "90vh",
            pr: 1,
            pb: 1,
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: 2,
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
              <CircularProgress aria-label="Ładowanie scenariuszy" />
            </Box>
          ) : scenarios.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                p: 6,
                bgcolor: "action.hover",
                borderRadius: 3,
              }}
            >
              <Typography variant="h6">
                {error ? "Lista jest niedostępna" : "Brak scenariuszy"}
              </Typography>
              <Typography color="text.secondary">
                {error
                  ? "Odśwież dane, aby spróbować ponownie."
                  : "Tutaj pojawią się dostępne scenariusze."}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {scenarios.map((scenario) => {
                const stations = asArray(scenario.stations);
                const entityCount = stations.reduce(
                  (sum, station) => sum + asArray(station.entities).length,
                  0,
                );
                const deleting = deletingId === scenario.id;
                return (
                  <Card
                    key={scenario.id}
                    variant="outlined"
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.background.main
                          : "rgba(255, 255, 255, 0.58)",
                      borderRadius: 3,
                      borderColor: "divider",
                      boxShadow: "0 4px 18px rgba(15,23,42,0.05)",
                      transition: "box-shadow 180ms",
                      "&:hover": {
                        boxShadow: "0 8px 28px rgba(15,23,42,0.10)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary">
                            SCENARIUSZ #{scenario.id}
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 750, overflowWrap: "anywhere" }}
                          >
                            {scenario.name || "Bez nazwy"}
                          </Typography>
                        </Box>
                        {activeTab === "my" && (
                          <Tooltip title="Usuń scenariusz">
                            <span>
                              <IconButton
                                color="error"
                                aria-label={`Usuń scenariusz ${scenario.name || scenario.id}`}
                                disabled={deletingId !== null}
                                onClick={() => deleteScenario(scenario.id)}
                              >
                                {deleting ? (
                                  <CircularProgress size={22} color="inherit" />
                                ) : (
                                  <DeleteOutlineIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        useFlexGap
                        flexWrap="wrap"
                        spacing={1}
                        sx={{ my: 2 }}
                      >
                        <Chip
                          size="small"
                          color="primary"
                          label={`Stacje: ${stations.length}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Obiekty: ${entityCount}`}
                        />
                        {/* <Chip
                          size="small"
                          variant="outlined"
                          label={`Limit: ${scenario.limit ?? "Brak"}`}
                        /> */}
                        {stations.length === 0 && (
                          <Chip size="small" label="Bez stacji" />
                        )}
                      </Stack>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
                          gap: 2,
                        }}
                      >
                        <Metadata label="Stworzony przez (id):">
                          {scenario.userId}
                        </Metadata>
                        <Metadata label="GUID scenariusza">
                          {scenario.GUID}
                        </Metadata>
                      </Box>

                      {stations.length > 0 && (
                        <Accordion
                          disableGutters
                          elevation={0}
                          sx={{
                            mt: 2,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: "12px !important",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            id={`stations-header-${scenario.id}`}
                            aria-controls={`stations-panel-${scenario.id}`}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              Szczegóły stacji ({stations.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={2}>
                              {stations.map((station) => (
                                <Box
                                  key={station.id}
                                  sx={{
                                    p: 2,
                                    bgcolor: "action.hover",
                                    borderRadius: 2,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 700,
                                      overflowWrap: "anywhere",
                                    }}
                                  >
                                    {station.stationName || "Stacja bez nazwy"}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    ID: {station.id} · Obiekty:{" "}
                                    {asArray(station.entities).length}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "1fr 1fr",
                                      },
                                      gap: 1.5,
                                      mt: 1.5,
                                    }}
                                  >
                                    <Metadata label="Wymiary (X × Y × Z)">{`${station.dimensionX ?? "—"} × ${station.dimensionY ?? "—"} × ${station.dimensionZ ?? "—"}`}</Metadata>
                                    {/* <Metadata label="Źródło">
                                      {station.existingSource || "Brak"}
                                    </Metadata> */}
                                    <Metadata label="Utworzono">
                                      {formatDate(station.createdAt)}
                                    </Metadata>
                                    <Metadata label="Zaktualizowano">
                                      {formatDate(station.updatedAt)}
                                    </Metadata>
                                  </Box>
                                  <Box sx={{ mt: 1.5 }}>
                                    <Metadata label="GUID stacji">
                                      {station.GUID}
                                    </Metadata>
                                  </Box>
                                  {asArray(station.entities).length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                      <Divider sx={{ mb: 1.5 }} />
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 1 }}
                                      >
                                        Obiekty
                                      </Typography>
                                      <Stack spacing={1}>
                                        {station.entities.map((entity) => (
                                          <Box
                                            key={entity.id}
                                            sx={{
                                              p: 1.5,
                                              bgcolor: "background.paper",
                                              borderRadius: 1.5,
                                              border: 1,
                                              borderColor: "divider",
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                overflowWrap: "anywhere",
                                              }}
                                            >
                                              {entity.name || "Bez nazwy"}
                                            </Typography>
                                            {/* <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              component="div"
                                            >
                                              ID: {entity.id}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              component="div"
                                            >
                                              Pozycja (X, Y, Z):{" "}
                                              {entity.posX ?? "—"},{" "}
                                              {entity.posY ?? "—"},{" "}
                                              {entity.posZ ?? "—"}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              component="div"
                                            >
                                              Obrót (X, Y, Z):{" "}
                                              {entity.rotX ?? "—"},{" "}
                                              {entity.rotY ?? "—"},{" "}
                                              {entity.rotZ ?? "—"}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              component="div"
                                              sx={{ overflowWrap: "anywhere" }}
                                            >
                                              GUID: {entity.GUID ?? "Brak"}
                                            </Typography> */}
                                          </Box>
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mt: 2.5,
                        }}
                      >
                        <Button
                          variant="contained"
                          disableElevation
                          endIcon={<ArrowForwardIcon />}
                          disabled={deleting}
                          sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
                          onClick={() => {
                            setSelectedScenario(scenario);
                            navigate(`/vr/scenarios/${scenario.id}`);
                          }}
                        >
                          Otwórz scenariusz
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
      <Footer />
    </>
  );
}

// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import Grid from "@mui/material/Grid";
// import ButtonGroup from "@mui/material/ButtonGroup";
// import {
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   CircularProgress,
//   Box,
// } from "@mui/material";
// import AddScenarioContainerDialog from "../components/utilities/AddScenarioContainerDialog";
// import { deleteContainer } from "../api/stationsApi";
// import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
// import IconButton from "@mui/material/IconButton";
// import { useSelector } from "react-redux";
// import api from "../../../API/interceptors";

// export default function ScenarioList({ setSelectedScenario }) {
//   const navigate = useNavigate();
//   const [myScenarios, setMyScenarios] = useState([]);
//   const [allScenarios, setAllScenarios] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [activeTab, setActiveTab] = useState("my");

//   // const token =
//   //   "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MzQ3LCJpYXQiOjE3Njk2ODEzNzAsImV4cCI6MTc2OTY4MjI3MH0.j6i2WPdDtaiUkmA__Trvm9smU0RNJI7OCDUdaveEw0k";

//   const { accessToken: jwt } = useSelector((state) => state.auth);

//   const token = jwt;

//   const fetchScenarios = async () => {
//     setLoading(true);

//     try {
//       const [myRes, allRes] = await Promise.all([
//         api(
//           `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/containers`,
//         ),
//         api(
//           `${process.env.REACT_APP_API_URL_VR_EDITOR}/scenario-builder/all-containers`,
//         ),
//       ]);

//       const myData = Array.isArray(myRes) ? myRes : myRes.data;
//       const allData = Array.isArray(allRes) ? allRes : allRes.data;

//       if (!Array.isArray(myData) || !Array.isArray(allData)) {
//         throw new Error("API nie zwróciło tablic scenariuszy");
//       }

//       setMyScenarios(myData);
//       setAllScenarios(allData);
//     } catch (err) {
//       console.error("Error fetching scenarios:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchScenarios();
//   }, []);

//   const openScenario = (scenario) => {
//     setSelectedScenario(scenario);
//     // navigate(`/scenario/${scenario.id}`);
//     navigate(`/vr/scenarios/${scenario.id}`);
//   };

//   if (loading) {
//     return (
//       <Box
//         display="flex"
//         justifyContent="center"
//         alignItems="center"
//         minHeight="50vh"
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   const renderScenarios = (scenarios) => {
//     console.log("scenarios", scenarios);
//     if (scenarios.length === 0) {
//       return <Typography color="textSecondary">Brak scenariuszy</Typography>;
//     }

//     const deleteScenario = async (id) => {
//       await deleteContainer(id);
//       fetchScenarios();
//     };

//     return (
//       <Box
//         sx={{
//           maxHeight: "70vh",
//           overflowY: "auto",
//           pr: 1,
//         }}
//       >
//         <Grid container spacing={2}>
//           {scenarios?.map((s) => (
//             <Grid item xs={12} key={s.id}>
//               <Card variant="outlined" sx={{ position: "relative" }}>
//                 {activeTab === "my" && (
//                   <IconButton
//                     size="small"
//                     onClick={() => deleteScenario(s.id)}
//                     sx={{
//                       position: "absolute",
//                       top: 8,
//                       right: 8,
//                       color: "error.main",
//                       "&:hover": {
//                         backgroundColor: "error.light",
//                         color: "#fff",
//                       },
//                     }}
//                   >
//                     <DeleteOutlineIcon fontSize="small" />
//                   </IconButton>
//                 )}

//                 <CardContent>
//                   <Typography variant="h6">{s.name}</Typography>

//                   <Typography color="textSecondary" sx={{ mb: 2 }}>
//                     ID: {s.id} | Limit: {s.limit ?? "Brak"}
//                   </Typography>

//                   <Button
//                     variant="contained"
//                     color="primary"
//                     onClick={() => openScenario(s)}
//                   >
//                     Otwórz
//                   </Button>
//                 </CardContent>
//               </Card>
//             </Grid>
//           ))}
//         </Grid>
//       </Box>
//     );
//   };

//   return (
//     <Box sx={{ maxWidth: "800px", margin: "0 auto", p: 2 }}>
//       <Box mb={3} textAlign="center">
//         <ButtonGroup variant="contained" aria-label="Basic button group">
//           <Button
//             onClick={() => setActiveTab("my")}
//             color={activeTab === "my" ? "primary" : "inherit"}
//           >
//             Moje scenariusze
//           </Button>
//           <Button
//             onClick={() => setActiveTab("all")}
//             color={activeTab === "all" ? "primary" : "inherit"}
//           >
//             Wszystkie scenariusze
//           </Button>
//         </ButtonGroup>
//       </Box>

//       {activeTab === "my" && (
//         <>
//           <Box
//             sx={{
//               display: "flex",
//               alignItems: "center",
//               mt: 5,
//               mb: 5,
//               verticalAlign: "middle",
//             }}
//           >
//             <Typography variant="h5">Moje scenariusze</Typography>

//             <AddScenarioContainerDialog
//               token={token}
//               onSuccess={fetchScenarios}
//             />
//           </Box>
//           {renderScenarios(myScenarios)}
//         </>
//       )}

//       {activeTab === "all" && (
//         <>
//           <Typography variant="h5" gutterBottom>
//             Wszystkie scenariusze
//           </Typography>
//           {renderScenarios(allScenarios)}
//         </>
//       )}

//       {activeTab === "other" && (
//         <>
//           <Typography variant="h5" gutterBottom>
//             Inne scenariusze
//           </Typography>
//           <Typography color="textSecondary">
//             Brak scenariuszy do wyświetlenia
//           </Typography>
//         </>
//       )}
//     </Box>
//   );
// }
